import classnames from 'classnames'
import * as React from 'react'
import { useRevalidator, useSearchParams } from 'react-router-dom'

import { intervalCallbackOnWindowVisible } from '../../../app/shared/documentVisibility'
import { useTrackPageView } from '../../hooks/use-track-pageview'
import { useCachedLoaderData } from '../../hooks/useCachedLoaderData'
import { QuoteOptionsTabMenu } from '../quote/components/QuoteOptionsTabMenu'
import { QuoteStickySubMenu } from '../quote/components/QuoteStickySubMenu'
import { OptionsViewSwitcher } from './components/OptionsViewSwitcher'
import { useOptionsQuery } from './hooks/useOptionsQuery'
import { ChainRoute } from './routes/ChainRoute'
import { ListRoute } from './routes/ListRoute'
import { PlotRoute } from './routes/PlotRoute'
import { OptionsLoaderData, OptionsSubpage, OptionsView } from './types'

const OPTIONS_REFRESH_INTERVAL = 60 * 1000

export default function Options() {
  useTrackPageView()

  const { data } = useCachedLoaderData<OptionsLoaderData | null>()
  const revalidator = useRevalidator()
  const [searchParams] = useSearchParams()
  const query = useOptionsQuery(searchParams)

  React.useEffect(() => {
    const unsubscribe = intervalCallbackOnWindowVisible({ interval: OPTIONS_REFRESH_INTERVAL }, () => {
      // Do a false check so that browsers which don’t support this just continue
      if (navigator.onLine === false) return
      revalidator.revalidate()
    })

    return unsubscribe
    // Reset the refetch interval after every expiry change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.expiry, query.strike])

  return FinvizSettings.hasOptionsGreeksEnabled ? (
    <>
      <QuoteStickySubMenu
        leftContent={<QuoteOptionsTabMenu />}
        rightContent={
          data?.options?.length ? (
            <OptionsViewSwitcher subpage={query.type} activeView={data?.view ?? OptionsView.ChainByDate} />
          ) : undefined
        }
      />
      <div className="font-sans">
        {data?.options?.length ? (
          <OptionsContent {...data} subpage={query.type} />
        ) : (
          <div className="fv-container flex h-60 items-center justify-center text-muted">
            Options are not available for this stock.
          </div>
        )}
      </div>
    </>
  ) : (
    <div className="font-sans">
      {data?.options?.length ? (
        <>
          <div
            className={classnames('fv-container flex items-center justify-end pt-3', {
              'pb-2.5': data?.view === OptionsView.Plot,
            })}
          >
            <OptionsViewSwitcher subpage={query.type} activeView={data?.view ?? OptionsView.ChainByDate} />
          </div>
          <OptionsContent {...data} subpage={query.type} />
        </>
      ) : (
        <div className="fv-container flex h-60 items-center justify-center text-muted">
          Options are not available for this stock.
        </div>
      )}
    </div>
  )
}

function OptionsContent(props: OptionsLoaderData & { subpage: OptionsSubpage; view: OptionsView }) {
  switch (props.view) {
    case OptionsView.Plot:
      return <PlotRoute {...props} />
    case OptionsView.ListByDate:
    case OptionsView.ListByStrike:
      return <ListRoute {...props} />
    case OptionsView.ChainByStrike:
    default:
      return <ChainRoute {...props} />
  }
}
